/**
 * @fileoverview Agenda event card — coloured card per event in the
 *               agenda list. Adapted from upstream: dialog wrapper
 *               removed; clicking the card now fires `onEventClick`.
 *               Job name + status pill added for Morgan KeyDate metadata.
 */

import { format, parseISO } from "date-fns";
import { cva, type VariantProps } from "class-variance-authority";
import { Briefcase, Clock, Text } from "lucide-react";

import { useCalendar } from "@/calendar/contexts/use-calendar";
import { CATEGORY_META } from "@/calendar/categoryMeta";
import { TaskMetaPills } from "@/calendar/components/task-meta-pills";

import { cn } from "@/utils/misc";

import type { IEvent } from "@/calendar/interfaces";

const agendaEventCardVariants = cva(
  "flex w-full select-none items-start gap-3 rounded-md border p-3 text-left text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
  {
    variants: {
      color: {
        blue: "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-300 [&_.event-dot]:fill-blue-600",
        green:
          "border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-300 [&_.event-dot]:fill-green-600",
        red: "border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300 [&_.event-dot]:fill-red-600",
        yellow:
          "border-yellow-200 bg-yellow-50 text-yellow-700 dark:border-yellow-800 dark:bg-yellow-950 dark:text-yellow-300 [&_.event-dot]:fill-yellow-600",
        purple:
          "border-purple-200 bg-purple-50 text-purple-700 dark:border-purple-800 dark:bg-purple-950 dark:text-purple-300 [&_.event-dot]:fill-purple-600",
        orange:
          "border-orange-200 bg-orange-50 text-orange-700 dark:border-orange-800 dark:bg-orange-950 dark:text-orange-300 [&_.event-dot]:fill-orange-600",
        gray: "border-neutral-200 bg-neutral-50 text-neutral-900 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-300 [&_.event-dot]:fill-neutral-600",
        pink: "border-pink-200 bg-pink-50 text-pink-700 dark:border-pink-800 dark:bg-pink-950 dark:text-pink-300 [&_.event-dot]:fill-pink-600",

        "blue-dot":
          "bg-neutral-50 dark:bg-neutral-900 [&_.event-dot]:fill-blue-600",
        "green-dot":
          "bg-neutral-50 dark:bg-neutral-900 [&_.event-dot]:fill-green-600",
        "red-dot":
          "bg-neutral-50 dark:bg-neutral-900 [&_.event-dot]:fill-red-600",
        "orange-dot":
          "bg-neutral-50 dark:bg-neutral-900 [&_.event-dot]:fill-orange-600",
        "purple-dot":
          "bg-neutral-50 dark:bg-neutral-900 [&_.event-dot]:fill-purple-600",
        "yellow-dot":
          "bg-neutral-50 dark:bg-neutral-900 [&_.event-dot]:fill-yellow-600",
        "gray-dot":
          "bg-neutral-50 dark:bg-neutral-900 [&_.event-dot]:fill-neutral-600",
        "pink-dot":
          "bg-neutral-50 dark:bg-neutral-900 [&_.event-dot]:fill-pink-600",
      },
    },
    defaultVariants: {
      color: "blue-dot",
    },
  },
);

interface IProps {
  event: IEvent;
  eventCurrentDay?: number;
  eventTotalDays?: number;
}

export function AgendaEventCard({
  event,
  eventCurrentDay,
  eventTotalDays,
}: IProps) {
  const { badgeVariant, onEventClick } = useCalendar();

  const startDate = parseISO(event.startDate);
  const endDate = parseISO(event.endDate);

  const color: VariantProps<typeof agendaEventCardVariants>["color"] =
    badgeVariant === "dot" ? `${event.color}-dot` : event.color;

  const isPlannedMarker = event.meta?.marker === "planned";
  const classes = cn(
    agendaEventCardVariants({ color }),
    isPlannedMarker && "border-dashed bg-transparent opacity-75",
  );

  const categoryKey = event.meta?.category ?? "custom";
  const meta = CATEGORY_META[categoryKey];
  const CategoryIcon = meta.icon;

  return (
    <button
      type="button"
      className={classes}
      onClick={() => onEventClick(event)}
    >
      {/* Leading category icon — explains what the colour means
          without forcing the user to memorise a legend. */}
      <span
        className={cn(
          "mt-0.5 inline-flex size-9 shrink-0 items-center justify-center rounded-lg border border-foreground/10 bg-background shadow-sm",
        )}
        title={meta.label}
      >
        <CategoryIcon className={cn("size-4", meta.iconFgClass)} />
      </span>

      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <div className="flex flex-wrap items-center gap-1.5">
          {["mixed", "dot"].includes(badgeVariant) && (
            <svg
              width="8"
              height="8"
              viewBox="0 0 8 8"
              className="event-dot shrink-0"
            >
              <circle cx="4" cy="4" r="4" />
            </svg>
          )}

          <p className="min-w-0 flex-1 font-medium">
            {isPlannedMarker && (
              <span className="mr-1 text-[10px] font-semibold uppercase tracking-wide opacity-70">
                Planned ·{" "}
              </span>
            )}
            {eventCurrentDay && eventTotalDays && eventTotalDays > 1 && (
              <span className="mr-1 text-xs opacity-70">
                Day {eventCurrentDay} of {eventTotalDays} •{" "}
              </span>
            )}
            {event.title}
          </p>
          <span className="shrink-0 text-[10px] font-medium opacity-70">
            · {meta.label}
          </span>
        </div>

        {event.meta?.jobName && (
          <div className="flex items-center gap-1">
            <Briefcase className="size-3 shrink-0 opacity-70" />
            <span
              className="truncate rounded border border-foreground/20 bg-background px-1.5 py-0.5 text-[10px] font-semibold text-foreground shadow-sm"
              title={event.meta.jobName}
            >
              {event.meta.jobName}
            </span>
          </div>
        )}

        <TaskMetaPills meta={event.meta} />

        {!event.meta?.allDay && (
          <div className="flex items-center gap-1 text-foreground/80">
            <Clock className="size-3 shrink-0" />
            <p className="text-xs">
              {format(startDate, "h:mm a")} - {format(endDate, "h:mm a")}
            </p>
          </div>
        )}

        {event.description && (
          <div className="flex items-start gap-1 text-foreground/80">
            <Text className="size-3 shrink-0 translate-y-0.5" />
            <p className="line-clamp-2 text-xs">{event.description}</p>
          </div>
        )}
      </div>
    </button>
  );
}
